/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react';
import { useState, useEffect } from 'react';
import { withMyTheme } from '../theme/theme';
import { mobileCss } from '../theme/isMobile';
import { CircularProgress } from '@mui/material';
import { Navigate } from 'react-router-dom';
import { MyButton } from '../components/button/MyButton';
import { OffersList, Offer } from './OffersList';
import { OfferEditForm } from './OfferEditForm';
import { decodeBase64ToUTF8, utf8_to_b64 } from './encodingText';

const ContainerStyle = withMyTheme((theme) => css`
    display: flex;
    flex-direction: column;
    align-items: center;
    padding: 2rem;
    min-height: 100vh;
    background-color: ${theme.palette.background.default};
`);

const ContentStyle = withMyTheme((theme) => css`
    background-color: ${theme.palette.primary.main};
    padding: 2rem;
    border-radius: 12px;
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.15);
    width: 100%;
    max-width: 900px;
    margin-top: 2rem;
    
    ${mobileCss(`
        padding: 1.5rem;
        max-width: 95%;
    `)}
`);

const TitleStyle = withMyTheme((theme) => css`
    font-size: 2rem;
    font-weight: 600;
    color: ${theme.palette.text.primary};
    margin-bottom: 1.5rem;
    text-align: center;
    font-family: ${theme.typography.h1.fontFamily};
`);

const LoadingContainerStyle = css`
    display: flex;
    justify-content: center;
    align-items: center;
    min-height: 300px;
`;

const ErrorMessageStyle = withMyTheme((theme) => css`
    color: ${theme.palette.error.main};
    margin: 2rem 0;
    text-align: center;
    font-family: ${theme.typography.body1.fontFamily};
    font-size: 1.2rem;
`);

const ButtonContainerStyle = css`
    display: flex;
    justify-content: center;
    margin-top: 2rem;
    gap: 1rem;
`;

const ButtonStyle = withMyTheme((theme) => css`
    background-color: ${theme.palette.primary.dark};
    color: ${theme.palette.primary.contrastText};
    &:hover {
        background-color: ${theme.palette.primary.main};
    }
`);

export const OffersAdmin = () => {
    const [offers, setOffers] = useState<Offer[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [errorMessage, setErrorMessage] = useState('');
    const [redirect, setRedirect] = useState(false);
    const [editingOffer, setEditingOffer] = useState<Offer | null>(null);
    const [isSaving, setIsSaving] = useState(false);

    const [username, setUsername] = useState<string | null>(null);
    const [token, setToken] = useState<string | null>(null);
    const [repoDetails, setRepoDetails] = useState({
        owner: 'agatasawickawebsite',
        repo: 'agata-sawicka-website-code',
        configPath: 'src/offersConfig/offersConfig.json',
        imagesPath: 'public/images'
    });

    useEffect(() => {
        const storedUsername = localStorage.getItem('github_username');
        const storedToken = localStorage.getItem('github_token');

        if (!storedUsername || !storedToken) {
            setRedirect(true);
            return;
        }

        setUsername(storedUsername);
        setToken(storedToken);
        setRepoDetails(prev => ({
            ...prev,
            owner: storedUsername
        }));

        fetchOffers(storedUsername, storedToken);
    }, []);

    const fetchOffers = async (username: string, token: string) => {
        try {
            setIsLoading(true);
            // Get the repository content
            const response = await fetch(
                `https://api.github.com/repos/${username}/${repoDetails.repo}/contents/${repoDetails.configPath}`,
                {
                    headers: {
                        'Authorization': `token ${token}`,
                        'Accept': 'application/vnd.github.v3+json'
                    }
                }
            );

            if (!response.ok) {
                throw new Error('Nie udało się pobrać pliku konfiguracyjnego. Sprawdź strukturę repozytorium.');
            }

            const fileData = await response.json();
            
            // const decodedContent = atob(fileData.content);
            const decodedContent = decodeBase64ToUTF8(fileData.content)
            
            // Parse JSON content
            const parsedOffers = JSON.parse(decodedContent);
            setOffers(parsedOffers);
            
            // Store the SHA for later updates
            localStorage.setItem('config_file_sha', fileData.sha);
        } catch (error) {
            setErrorMessage(error instanceof Error ? error.message : 'Wystąpił nieznany błąd');
        } finally {
            setIsLoading(false);
        }
    };

    const handleLogout = () => {
        localStorage.removeItem('github_username');
        localStorage.removeItem('github_token');
        localStorage.removeItem('config_file_sha');
        setRedirect(true);
    };

    const handleEditOffer = (offer: Offer) => {
        setEditingOffer(offer);
    };
    
    const handleAddOffer = (newOffer: Offer) => {
        setEditingOffer(newOffer);
    };

    const handleCancelEdit = () => {
        setEditingOffer(null);
    };
    
    const handleRemoveOffer = async (offerToRemove: Offer) => {
        if (!username || !token) {
            setErrorMessage('Wymagane uwierzytelnienie');
            return;
        }
        
        try {
            // Get the SHA of the config file
            const configFileSha = localStorage.getItem('config_file_sha');
            if (!configFileSha) {
                throw new Error('Nie znaleziono SHA pliku konfiguracyjnego. Odśwież stronę.');
            }
            
            // Filter out the offer to remove
            const updatedOffers = offers.filter(offer => offer.id !== offerToRemove.id);
            
            // Update the config file in GitHub
            const response = await fetch(
                `https://api.github.com/repos/${username}/${repoDetails.repo}/contents/${repoDetails.configPath}`,
                {
                    method: 'PUT',
                    headers: {
                        'Authorization': `token ${token}`,
                        'Accept': 'application/vnd.github.v3+json',
                        'Content-Type': 'application/json'
                    },
                    body: JSON.stringify({
                        message: `Usunięcie oferty: ${offerToRemove.name}`,
                        content: utf8_to_b64(JSON.stringify(updatedOffers, null, 2)),
                        sha: configFileSha
                    })
                }
            );
            
            if (!response.ok) {
                const error = await response.json();
                throw new Error(`Nie udało się zaktualizować konfiguracji: ${error.message}`);
            }
            
            const responseData = await response.json();
            
            // Update the SHA for future updates
            localStorage.setItem('config_file_sha', responseData.content.sha);
            
            // Try to delete associated images if they exist
            const imagesToDelete = [];
            
            // Add main image to deletion list
            if (offerToRemove.main_image && offerToRemove.main_image?.url) {
                imagesToDelete.push(offerToRemove.main_image.url);
            }
            
            // Add desktop image to deletion list if it exists
            if (offerToRemove.image_desktop?.url) {
                imagesToDelete.push(offerToRemove.image_desktop.url);
            }
            
            // Delete each image
            for (const imagePath of imagesToDelete) {
                try {
                    // Clean path and create full path
                    const cleanPath = imagePath.startsWith('/') ? imagePath.substring(1) : imagePath;
                    const fullPath = `public/${cleanPath}`;
                    
                    // Get file metadata to get SHA
                    const imageResponse = await fetch(
                        `https://api.github.com/repos/${username}/${repoDetails.repo}/contents/${fullPath}`,
                        {
                            headers: {
                                'Authorization': `token ${token}`,
                                'Accept': 'application/vnd.github.v3+json'
                            }
                        }
                    );
                    
                    if (imageResponse.ok) {
                        const imageData = await imageResponse.json();
                        
                        // Delete the file
                        await fetch(
                            `https://api.github.com/repos/${username}/${repoDetails.repo}/contents/${fullPath}`,
                            {
                                method: 'DELETE',
                                headers: {
                                    'Authorization': `token ${token}`,
                                    'Accept': 'application/vnd.github.v3+json',
                                    'Content-Type': 'application/json'
                                },
                                body: JSON.stringify({
                                    message: `Usunięcie zdjęcia: ${imagePath}`,
                                    sha: imageData.sha
                                })
                            }
                        );
                    }
                } catch (imageError) {
                    console.error(`Błąd podczas usuwania zdjęcia ${imagePath}:`, imageError);
                    // Continue with other deletions even if one fails
                }
            }
            
            // Update local state
            setOffers(updatedOffers);
            
        } catch (error) {
            console.error('Error removing offer:', error);
            throw error;
        }
    };

    const uploadImage = async (file: File, path: string): Promise<string> => {
        if (!username || !token) {
            throw new Error('Wymagane uwierzytelnienie');
        }
        
        // Create a new filename to avoid caching issues
        const fileExtension = file.name.split('.').pop();
        const timestamp = new Date().getTime();
        const newFileName = `${path.replace(/\.[^/.]+$/, '')}_${timestamp}.${fileExtension}`;
        const fullPath = `${repoDetails.imagesPath}/${newFileName}`;
        
        // Convert file to base64
        const base64Content = await new Promise<string>((resolve) => {
            const reader = new FileReader();
            reader.onloadend = () => {
                const result = reader.result as string;
                // Remove the data:image/xxx;base64, prefix
                const base64 = result.split(',')[1];
                resolve(base64);
            };
            reader.readAsDataURL(file);
        });
        
        // Upload file to GitHub
        const response = await fetch(
            `https://api.github.com/repos/${username}/${repoDetails.repo}/contents/${fullPath}`,
            {
                method: 'PUT',
                headers: {
                    'Authorization': `token ${token}`,
                    'Accept': 'application/vnd.github.v3+json',
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    message: `Dodanie zdjęcia: ${newFileName}`,
                    content: base64Content
                })
            }
        );
        
        if (!response.ok) {
            const error = await response.json();
            throw new Error(`Nie udało się przesłać zdjęcia: ${error.message}`);
        }
        
        return newFileName;
    };

    const handleSaveOffer = async (
        updatedOffer: any, 
        mainImageFile?: File, 
        desktopImageFile?: File,
        additionalImageFiles?: File[],
    ) => {
        if (!username || !token) {
            setErrorMessage('Wymagane uwierzytelnienie');
            return;
        }
        
        // Validate required fields
        if (!updatedOffer.name || !updatedOffer.slug || !updatedOffer.description || !updatedOffer.order) {
            alert('Wypełnij wszystkie wymagane pola formularza.');
            return;
        }
        
        // Check if this is a new offer
        const isNewOffer = !offers.some(offer => offer.id === updatedOffer.id);
        
        // Validate images for new offers
        if (isNewOffer) {
            if (!mainImageFile) {
                alert('Dodaj zdjęcie dla wersji mobilnej.');
                return;
            }
            if (!desktopImageFile) {
                alert('Dodaj zdjęcie dla wersji desktopowej.');
                return;
            }
        } else {
            // For existing offers, check if they have images already if no new ones provided
            if (!mainImageFile && (!updatedOffer.main_image || !updatedOffer.main_image?.url)) {
                alert('Dodaj zdjęcie dla wersji mobilnej.');
                return;
            }
            if (!desktopImageFile && (!updatedOffer.image_desktop || !updatedOffer.image_desktop.url)) {
                alert('Dodaj zdjęcie dla wersji desktopowej.');
                return;
            }
        }
        
        setIsSaving(true);
        
        try {
            // Handle image uploads if provided
            if (mainImageFile) {
                const newFileName = await uploadImage(mainImageFile, mainImageFile.name);
                updatedOffer.main_image.url = `images/${newFileName}`;
                updatedOffer.main_image.alt = newFileName;
            }
            
            if (desktopImageFile && updatedOffer.image_desktop) {
                const newFileName = await uploadImage(desktopImageFile, desktopImageFile.name);
                updatedOffer.image_desktop.url = `images/${newFileName}`;
            }

            if(!!additionalImageFiles && additionalImageFiles.length > 0) {
                for (const file of additionalImageFiles) {
                    const newFileName = await uploadImage(file, file.name);
                    updatedOffer.additional_images.push({ url: `images/${newFileName}`, alt: updatedOffer.name });
                }
            }
            
            // Update or add the offer to the offers array
            let updatedOffers;
            if (isNewOffer) {
                updatedOffers = [...offers, updatedOffer];
            } else {
                updatedOffers = offers.map(offer => 
                    offer.id === updatedOffer.id ? updatedOffer : offer
                );
            }
            
            // Get the SHA of the config file
            const configFileSha = localStorage.getItem('config_file_sha');
            if (!configFileSha) {
                throw new Error('Nie znaleziono SHA pliku konfiguracyjnego. Odśwież stronę.');
            }
            
            // Update the config file in GitHub
            const response = await fetch(
                `https://api.github.com/repos/${username}/${repoDetails.repo}/contents/${repoDetails.configPath}`,
                {
                    method: 'PUT',
                    headers: {
                        'Authorization': `token ${token}`,
                        'Accept': 'application/vnd.github.v3+json',
                        'Content-Type': 'application/json'
                    },
                    body: JSON.stringify({
                        message: isNewOffer 
                            ? `Dodanie nowej oferty: ${updatedOffer.name}` 
                            : `Aktualizacja oferty: ${updatedOffer.name}`,
                        content: utf8_to_b64(JSON.stringify(updatedOffers, null, 2)),
                        sha: configFileSha
                    })
                }
            );
            
            if (!response.ok) {
                const error = await response.json();
                throw new Error(`Nie udało się ${isNewOffer ? 'dodać' : 'zaktualizować'} konfiguracji: ${error.message}`);
            }
            
            const responseData = await response.json();
            
            // Update the SHA for future updates
            localStorage.setItem('config_file_sha', responseData.content.sha);
            
            // Update local state
            setOffers(updatedOffers);
            setEditingOffer(null);
            
            alert(isNewOffer 
                ? 'Nowa oferta została dodana pomyślnie! Zmiany zostaną wprowadzone wkrótce na stronie (zazwyczaj około 15 minut)' 
                : 'Oferta została zaktualizowana pomyślnie! Zmiany zostaną wprowadzone wkrótce na stronie (zazwyczaj około 15 minut)');
        } catch (error) {
            console.error('Błąd podczas zapisywania oferty:', error);
            setErrorMessage(error instanceof Error ? error.message : 'Nie udało się zapisać zmian');
        } finally {
            setIsSaving(false);
        }
    };

    if (redirect) {
        return <Navigate to="/super-admin" />;
    }

    return (
        <div css={ContainerStyle}>
            <h1 css={TitleStyle}>Panel administracyjny ofert</h1>
            
            <div css={ContentStyle}>
                {isLoading ? (
                    <div css={LoadingContainerStyle}>
                        <CircularProgress />
                    </div>
                ) : errorMessage ? (
                    <div css={ErrorMessageStyle}>{errorMessage}</div>
                ) : editingOffer ? (
                    <OfferEditForm 
                        offer={editingOffer}
                        onSave={handleSaveOffer}
                        onCancel={handleCancelEdit}
                    />
                ) : (
                    <OffersList 
                        offers={offers}
                        onEditOffer={handleEditOffer}
                        onAddOffer={handleAddOffer}
                        onRemoveOffer={handleRemoveOffer}
                        onSignOut={handleLogout}
                    />
                )}
            </div>
        </div>
    );
};